import debounce from 'lodash/debounce';
import { useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

interface IUseSimpleSearchOptions {
  debounceWait?: number;
  minInputLength?: number;
}

const defaultOptions: IUseSimpleSearchOptions = {
  debounceWait: 500,
  minInputLength: 3,
};

function validate(value, minInputLength) {
  return value?.length >= minInputLength ? value : null;
}

export function useLocalSearch(options: IUseSimpleSearchOptions = {}) {
  const { debounceWait, minInputLength } = {
    ...defaultOptions,
    ...options,
  };
  const ref = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState(null);

  useIsomorphicLayoutEffect(() => {
    const input = ref.current;

    const changeHandler = debounce(
      (event: Event) => {
        const target = event.target as HTMLInputElement;
        const value = validate(target.value, minInputLength);

        setSearchTerm(value);
      },
      debounceWait,
      { leading: false }
    );

    if (input) {
      input.addEventListener('input', changeHandler);
    }

    return () => {
      if (input) {
        input.removeEventListener('input', changeHandler);
      }
    };
  }, [setSearchTerm, debounceWait, minInputLength]);

  return {
    searchTerm,
    ref,
  };
}
